import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Button,
  Spinner,
  Form,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import Slider from '@material-ui/core/Slider';

import { segmentPropType, panelsPropType } from '../props';

const toFiniteNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const Revise = ({
  currentPanel,
  selectedRoof,
  panels,
  segments,
  panelAzimuthHandler,
  clearPanels,
  placementHandler,
  panelPlacementFillMode,
  setPanelPlacementFillMode,
  panelPlacementLayoutMode,
  setPanelPlacementLayoutMode,
  panelPlacementIsRunning,
}) => {
  const segment = segments[selectedRoof];
  const panel = segment ? panels.find(({ id }) => id === segment.id) : null;
  const panelName =
    currentPanel?.name || currentPanel?.model || 'Not selected';
  const panelPower = currentPanel?.power ? ` (${currentPanel.power}W)` : '';
  const panelAzimuth = panel ? toFiniteNumber(panel.azimuth) : null;
  const segmentAzimuth = segment ? toFiniteNumber(segment.azimuth) : null;
  const canEditAzimuth =
    panelAzimuth !== null && segmentAzimuth !== null;

  return (
    <>
      <p className="mb-2">
        <strong>Current Panel:</strong> {panelName}
        {panelPower}
      </p>

      {!segment && (
        <p className="mb-2">
          Click on a roof segment to fill panels or adjust their azimuth.
        </p>
      )}

      {segment && panel && canEditAzimuth && (
        <>
          <h6 className="font-weight-bold">Azimuth</h6>
          <Row>
            <Col sm={6}>
              <Form.Control
                value={Math.round(panelAzimuth * 2) / 2}
                readOnly
              />
            </Col>
          </Row>
          <Slider
            value={panelAzimuth}
            min={segmentAzimuth - 7.5}
            max={segmentAzimuth + 7.5}
            step={0.5}
            aria-labelledby="Azimuth"
            onChange={(e, azimuth) => {
              const nextAzimuth = Number(azimuth);
              if (!Number.isFinite(nextAzimuth)) return;
              panelAzimuthHandler(selectedRoof, +nextAzimuth.toFixed(2));
            }}
            disabled={panelPlacementIsRunning}
          />
        </>
      )}

      {segment && (
        <>
          <Row className="mt-2 mr-3">
            <Col>
              <Form.Control
                as="select"
                value={panelPlacementFillMode}
                onChange={(e) =>
                  setPanelPlacementFillMode(Number(e.target.value))
                }
                disabled={panelPlacementIsRunning}
              >
                <option value={3}>Vertical First</option>
                <option value={4}>Horizontal First</option>
                <option value={2}>Vertical</option>
                <option value={1}>Horizontal</option>
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-1 mr-3">
            <Col>
              <ToggleButtonGroup type="checkbox" style={{ width: '100%' }}>
                <ToggleButton
                  value={1}
                  disabled={panelPlacementIsRunning}
                  style={{
                    color: panelPlacementLayoutMode === 1 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(1)}
                >
                  Auto
                </ToggleButton>
                <ToggleButton
                  value={2}
                  disabled={panelPlacementIsRunning}
                  style={{
                    color: panelPlacementLayoutMode === 2 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(2)}
                >
                  Grid-Mode
                </ToggleButton>
                <ToggleButton
                  value={3}
                  disabled={panelPlacementIsRunning}
                  style={{
                    color: panelPlacementLayoutMode === 3 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(3)}
                >
                  Brick-Mode
                </ToggleButton>
                <ToggleButton
                  value={4}
                  disabled={panelPlacementIsRunning}
                  style={{
                    color: panelPlacementLayoutMode === 4 ? 'white' : 'gray',
                  }}
                  onClick={() => setPanelPlacementLayoutMode(4)}
                >
                  Compact
                </ToggleButton>
              </ToggleButtonGroup>
            </Col>
          </Row>
          <Row className="mt-1 mr-3">
            <Col>
              <ButtonGroup style={{ width: '100%' }}>
                <Button
                  onClick={(e) =>
                    placementHandler(
                      e,
                      false,
                      panelPlacementFillMode,
                      panelPlacementLayoutMode
                    )
                  }
                  disabled={!currentPanel || panelPlacementIsRunning}
                >
                  {panelPlacementIsRunning ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-2" />
                      Filling...
                    </>
                  ) : (
                    'Fill Panels'
                  )}
                </Button>
                <Button
                  onClick={() => clearPanels && clearPanels()}
                  disabled={!currentPanel || panelPlacementIsRunning}
                >
                  Clear
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

Revise.propTypes = {
  currentPanel: PropTypes.object,
  selectedRoof: PropTypes.number.isRequired,
  panels: panelsPropType.isRequired,
  segments: segmentPropType.isRequired,
  panelAzimuthHandler: PropTypes.func.isRequired,
  clearPanels: PropTypes.func,
  placementHandler: PropTypes.func.isRequired,
  panelPlacementFillMode: PropTypes.number.isRequired,
  setPanelPlacementFillMode: PropTypes.func.isRequired,
  panelPlacementLayoutMode: PropTypes.number.isRequired,
  setPanelPlacementLayoutMode: PropTypes.func.isRequired,
  panelPlacementIsRunning: PropTypes.bool.isRequired,
};

Revise.defaultProps = {
  currentPanel: null,
  clearPanels: null,
};

export default Revise;
